import { Fragment, useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { Header } from './Header';
import {
  LayoutDashboard,
  ClipboardList,
  CheckSquare,
  Users,
  BookOpen,
  Settings,
  Coins,
  FileText,
  Activity,
  FileBarChart,
  Gift,
  Megaphone,
  Trophy,
  BadgeCheck,
  Menu,
  X,
} from 'lucide-react';

const adminNavItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/tasks', icon: ClipboardList, label: 'Tasks' },
  { path: '/admin/reviews', icon: CheckSquare, label: 'Reviews' },
  { path: '/admin/payments', icon: Coins, label: 'Payments' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/local-guide', icon: BadgeCheck, label: 'Local Guide' },
  { path: '/admin/activity', icon: Activity, label: 'Activity' },
  { path: '/admin/reports', icon: FileBarChart, label: 'Reports' },
  { path: '/admin/performance', icon: Trophy, label: 'Performance' },
  { path: '/admin/referrals', icon: Gift, label: 'Referrals' },
  { path: '/admin/announcements', icon: Megaphone, label: 'Announcements' },
  { path: '/admin/guide', icon: BookOpen, label: 'User Guide' },
  { path: '/admin/logs', icon: FileText, label: 'Logs' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

export function AdminLayout() {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string, exact?: boolean) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const navLinkClasses = (path: string, exact?: boolean) =>
    `flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
      isActive(path, exact)
        ? 'bg-primary-50 text-primary-700'
        : 'text-gray-600 hover:bg-gray-50'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Mobile header bar - visible only on mobile */}
      <div className="md:hidden bg-primary-600 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="p-1 rounded-lg hover:bg-primary-500 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6 text-white" />
        </button>
        <span className="text-white font-extrabold">Admin Panel</span>
        <div className="w-8" /> {/* Spacer for centering */}
      </div>

      <div className="flex">
        {/* Desktop sidebar - hidden on mobile */}
        <aside className="hidden md:block w-64 min-h-[calc(100vh-4rem)] bg-white border-r border-gray-200">
          {/* Admin Panel Branding */}
          <div className="px-4 py-4 bg-primary-600">
            <Link to="/admin" className="flex items-center space-x-2">
              <img src="/white-logo.png" alt="Review Like a Pro" className="w-8 h-8" />
              <span className="text-white font-extrabold">Admin Panel</span>
            </Link>
          </div>
          <nav className="p-4 space-y-1">
            {adminNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={navLinkClasses(item.path, item.exact)}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>

        {/* Mobile slide-out drawer */}
        <Transition show={isMobileMenuOpen} as={Fragment}>
          <div className="md:hidden">
            {/* Backdrop */}
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setIsMobileMenuOpen(false)}
              />
            </Transition.Child>

            {/* Drawer panel */}
            <Transition.Child
              as={Fragment}
              enter="transition-transform ease-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition-transform ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <aside className="fixed inset-y-0 left-0 w-64 bg-white z-50 shadow-xl">
                {/* Close button header */}
                <div className="px-4 py-4 bg-primary-600 flex items-center justify-between">
                  <Link to="/admin" className="flex items-center space-x-2">
                    <img src="/white-logo.png" alt="Review Like a Pro" className="w-8 h-8" />
                    <span className="text-white font-extrabold">Admin Panel</span>
                  </Link>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-1 rounded-lg hover:bg-primary-500 transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>

                {/* Navigation items */}
                <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-4rem)]">
                  {adminNavItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={navLinkClasses(item.path, item.exact)}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  ))}
                </nav>
              </aside>
            </Transition.Child>
          </div>
        </Transition>

        <main className="flex-1 p-2 md:p-4 lg:p-6 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
